/*
  Everything we send to GTM/GA passes through here.

  Report tokens are bearer credentials: whoever reads one can open the report.
  They appear both as a path segment on short links (/t/, /i/) and as a
  ?token= query param on every report page. The query is therefore handled as
  an allowlist rather than a denylist, so a param added later is dropped by
  default instead of leaking until someone notices.
*/

// Confirmed with the GA team. Everything not listed here is dropped.
export const TRACKED_QUERY_PARAMS: readonly string[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "fbclid",
  "msclkid",
];

/*
  The named masks come first so a known segment keeps its own label, then the
  guid catch-all sweeps up anything else that looks like an identifier.

  The catch-all is what keeps this list from going stale: a route added later
  that carries a document or report guid is masked without anyone remembering
  to extend this file. It does not cover every shape — a numeric id or a
  non-guid token in a new path segment would still need its own entry here.

  All patterns are global: a path may hold more than one identifier.
*/
const PATH_MASKS: readonly [RegExp, string][] = [
  [/\/t\/[^/?#]+/g, "/t/:token"],
  [/\/i\/[^/?#]+/g, "/i/:token"],
  [/\/signer\/[^/?#]+/g, "/signer/:hash"],
  [
    /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi,
    "/:guid",
  ],
];

function maskPathname(pathname: string): string {
  return PATH_MASKS.reduce(
    (masked, [pattern, replacement]) => masked.replace(pattern, replacement),
    pathname,
  );
}

function allowlistSearch(search: string | undefined): string {
  if (!search) return "";

  const source = new URLSearchParams(search);
  const tracked = new URLSearchParams();

  for (const param of TRACKED_QUERY_PARAMS) {
    for (const value of source.getAll(param)) {
      tracked.append(param, value);
    }
  }

  const query = tracked.toString();
  return query ? `?${query}` : "";
}

export function anonymizeLocation(location: {
  pathname: string;
  search?: string;
  /*
    Accepted so a whole Location (react-router's or the DOM's) can be passed
    straight in. Deliberately unused: fragments add noise and never carry
    anything GA needs.
  */
  hash?: string;
}): string {
  return `${maskPathname(location.pathname)}${allowlistSearch(location.search)}`;
}

export function anonymizeAbsoluteUrl(url: string): string | undefined {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Fail closed: better to send nothing than an unexamined url.
    return undefined;
  }

  return `${parsed.origin}${anonymizeLocation(parsed)}`;
}
